<script setup lang="ts">
  import { computed, onMounted, onUnmounted, ref } from 'vue'
  import {
    type Document,
    type DocumentEditTokenResponse,
    ResourceTypeEnum,
    DocumentsService
  } from '@/types/generated'
  import {
    DocumentsService as AdminDocumentsService,
    WordDocumentTemplatesService
  } from '@/types/generated-admin/services.gen'
  import type { WordDocumentTemplatesCreateEditTokenCreateData } from '@/types/generated-admin'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useLocaleStore } from '@/store/localStore'

  interface Props {
    documentUuid: string
    wordTemplateUuid?: string
    resourceType?: ResourceTypeEnum
    gobackUrl?: string
    checkForNavbar?: boolean
    useAdminApi?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    resourceType: ResourceTypeEnum.DOCUMENT,
    checkForNavbar: true,
    useAdminApi: false
  })

  interface OnlyOfficeConfig {
    document: {
      fileType: string
      key: string
      title: string
      url: string
      permissions: {
        edit: boolean
        download: boolean
        print: boolean
        review: boolean
        chat: boolean
        comments: boolean
      }
    }
    documentType: string
    editorConfig: {
      mode: string
      lang: string
      callbackUrl: string
      user: {
        id: string
        name: string
      }
      customization: {
        goback?: {
          url: string
        }
        autosave: boolean
        compactHeader: boolean
        compactToolbar: boolean
        compatibleFeatures: boolean
        customer: {
          address: string
          info: string
          logo: string
          logoDark: string
          mail: string
          name: string
          phone: string
          www: string
        }
        features: {
          featureTips: boolean
          roles: boolean
          feedback: boolean
          tabStyle: 'line' | 'fill'
          tabBackground: 'toolbar' | 'header'
        }
        integrationMode: 'embed'
        forcesave: boolean
        notes: boolean
        hideRightMenu: boolean
        help: boolean
        plugins: boolean
        uiTheme: string
      }
    }
    height: string
    width: string
    token: string
    events: {
      onError: (event: any) => void
      onDocumentReady: () => void
      onInfo: (event: any) => void
    }
  }

  interface OnlyOfficeEditor {
    destroyEditor: () => void
  }

  declare global {
    interface Window {
      DocsAPI: {
        DocEditor: new (elementId: string, config: OnlyOfficeConfig) => OnlyOfficeEditor
      }
      docEditor: OnlyOfficeEditor | null
    }
  }

  const userStore = useUserStore()
  const localeStore = useLocaleStore()
  const hasNavbar = ref(false)
  const hasSidebar = ref(false)
  const documentData = ref<Document | null>(null)
  const editToken = ref<string | null>(null)
  const tokenExpiresAt = ref<string | null>(null)
  const onlyofficeJwt = ref<string | null>(null)
  const serverDocumentKey = ref<string | null>(null)
  const serverDocumentUrl = ref<string | null>(null)
  const serverFileType = ref<string | null>(null)
  const serverTitle = ref<string | null>(null)
  const tokenRefreshTimer = ref<NodeJS.Timeout | null>(null)
  const isRefreshingToken = ref(false)
  const isLoading = ref(true)
  const navbarChecker = ref<NodeJS.Timeout | null>(null)

  const isTokenValid = computed(() => {
    if (!editToken.value || !tokenExpiresAt.value) return false
    const now = new Date()
    const expiresAt = new Date(tokenExpiresAt.value)
    return now < expiresAt
  })

  const needsRefresh = computed(() => {
    if (!tokenExpiresAt.value) return false
    const now = new Date()
    const expiresAt = new Date(tokenExpiresAt.value)
    const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000)
    return fiveMinutesFromNow >= expiresAt
  })

  function detectNavbar() {
    if (!props.checkForNavbar) return

    const navbar = document.querySelector(
      '.navbar.navbar-expand-md.navbar-light.d-print-none.bg-danger'
    )
    hasNavbar.value = !!navbar

    if (!hasNavbar.value) {
      const dangerNavbar = document.querySelector('.navbar.bg-danger')
      hasNavbar.value = !!dangerNavbar
    }

    const sidebar = document.getElementById('admin-sidebar')
    hasSidebar.value = !!sidebar
  }

  async function fetchDocument() {
    isLoading.value = true

    const service = props.useAdminApi ? AdminDocumentsService : DocumentsService
    const response = await service.documentsRetrieve({
      uuid: props.documentUuid
    })
    documentData.value = response as Document

    isLoading.value = false
  }

  async function fetchEditToken() {
    if (isRefreshingToken.value) {
      return false
    }

    try {
      isRefreshingToken.value = true
      let response: DocumentEditTokenResponse

      const templateUuid =
        props.wordTemplateUuid ||
        (props.resourceType === ResourceTypeEnum.WORD_TEMPLATE ? props.documentUuid : null)

      if (templateUuid) {
        // The admin API's OpenAPI spec incorrectly requires formData (class-level schema artifact).
        // The backend create_edit_token action only needs the UUID from the URL path.
        response = (await WordDocumentTemplatesService.wordDocumentTemplatesCreateEditTokenCreate({
          uuid: templateUuid
        } as WordDocumentTemplatesCreateEditTokenCreateData)) as DocumentEditTokenResponse
      } else {
        response = await DocumentsService.documentsCreateEditTokenCreate({
          uuid: props.documentUuid
        })
      }

      editToken.value = response.token
      tokenExpiresAt.value = response.expires_at
      onlyofficeJwt.value = response.onlyoffice_jwt
      serverDocumentKey.value = response.document_key
      serverDocumentUrl.value = response.document_url
      serverFileType.value = response.file_type
      serverTitle.value = response.title
      scheduleTokenRefresh()
      return true
    } finally {
      isRefreshingToken.value = false
    }
  }

  function scheduleTokenRefresh() {
    if (tokenRefreshTimer.value) {
      clearTimeout(tokenRefreshTimer.value)
    }

    const now = new Date()
    const expiresAt = new Date(tokenExpiresAt.value)
    const refreshAt = new Date(expiresAt.getTime() - 5 * 60 * 1000)
    const timeUntilRefresh = refreshAt.getTime() - now.getTime()

    if (timeUntilRefresh > 0) {
      tokenRefreshTimer.value = setTimeout(async () => {
        await fetchEditToken()
      }, timeUntilRefresh)
    }
  }

  async function ensureValidToken(): Promise<boolean> {
    if (isTokenValid.value && !needsRefresh.value) {
      return true
    }
    return await fetchEditToken()
  }

  async function initializeOnlyOffice() {
    await ensureValidToken()

    // Check if script is already loaded
    const existingScript = document.querySelector(
      'script[src="/onlyoffice/web-apps/apps/api/documents/api.js"]'
    )
    if (existingScript && window.DocsAPI) {
      // Script already loaded, initialize directly
      initializeEditor()
      return
    }

    const script = document.createElement('script')
    script.src = '/onlyoffice/web-apps/apps/api/documents/api.js'

    script.onload = () => {
      initializeEditor()
    }

    script.onerror = () => {
      console.error('Failed to load OnlyOffice API script')
    }

    document.head.appendChild(script)
  }

  function initializeEditor() {
    let filename: string
    let url: string
    let title: string
    let uuid: string

    const doc = documentData.value!
    filename = doc.filename.toLowerCase()
    url = doc.url
    title = doc.title || doc.filename
    uuid = doc.uuid

    let fileType = 'docx'
    let documentType = 'word'

    if (filename.endsWith('.docx') || filename.endsWith('.doc')) {
      fileType = filename.endsWith('.docx') ? 'docx' : 'doc'
      documentType = 'word'
    } else if (filename.endsWith('.xlsx') || filename.endsWith('.xls')) {
      fileType = filename.endsWith('.xlsx') ? 'xlsx' : 'xls'
      documentType = 'cell'
    } else if (filename.endsWith('.pptx') || filename.endsWith('.ppt')) {
      fileType = filename.endsWith('.pptx') ? 'pptx' : 'ppt'
      documentType = 'slide'
    }

    let callbackHost = window.location.origin
    if (url.includes('localhost')) {
      url = url.replace('localhost', 'caddy')
      callbackHost = 'http://app:8000'
    }
    let callbackUrl = `${callbackHost}/d/edit/${editToken.value}`

    const currentLocale = localeStore.getLocale
    const region = currentLocale === 'de' ? 'de-DE' : 'en-US'

    window.docEditor = new window.DocsAPI.DocEditor('office-editor-placeholder', {
      token: onlyofficeJwt.value!,
      document: {
        fileType: serverFileType.value!,
        key: serverDocumentKey.value!,
        title: serverTitle.value!,
        url: serverDocumentUrl.value!,
        permissions: {
          edit: true,
          download: false,
          print: false,
          review: false,
          chat: false,
          comments: false
        }
      },
      documentType: documentType,
      editorConfig: {
        mode: 'edit',
        lang: currentLocale,
        region: region,
        callbackUrl: callbackUrl,
        user: {
          id: userStore.user!.uuid,
          name: `${userStore.user!.first_name} ${userStore.user!.last_name}`
        },
        customization: {
          ...(props.gobackUrl && {
            goback: {
              url: props.gobackUrl
            }
          }),
          macros: false,
          autosave: true,
          compactHeader: true,
          compactToolbar: false,
          compatibleFeatures: true,
          customer: {
            address: 'Hohenzollernring 89-93, 50672 Köln',
            info: 'Das KI-Sekretariat für Rechtsanwälte',
            logo: '/static/images/logo.svg',
            logoDark: '/static/images/logo.svg',
            mail: 'hello@jupus.de',
            name: 'JUPUS GmbH',
            phone: '+49 32 221855439',
            www: 'jupus.de'
          },
          features: {
            featureTips: false,
            roles: false,
            feedback: false,
            tabStyle: 'line',
            tabBackground: 'toolbar'
          },
          forcesave: true,
          notes: false,
          hideRightMenu: true,
          help: false,
          integrationMode: 'embed',
          logo: {
            image: window.location.origin + '/static/images/logo.svg',
            imageDark: window.location.origin + '/static/images/logo.svg',
            imageLight: window.location.origin + '/static/images/logo.svg',
            url: '', // Empty string to make logo non-clickable
            visible: true
          },
          plugins: false,
          review: {
            hideReviewDisplay: true,
            showReviewChanges: false,
            reviewDisplay: 'markup'
          },
          toolbarHideFileName: true,
          uiTheme: 'theme-white'
        }
      },
      height: '100%',
      width: '100%',
      events: {
        onError: function (event: any) {
          console.error('OnlyOffice Error:', event)
        },
        onDocumentReady: function () {},
        onInfo: function (event: any) {
          // onInfo can contain save-related information
          // Check if this is an autosave/forcesave event
          if (event?.data) {
            const data = event.data
            // OnlyOffice may send info about saves in the onInfo event
            // Look for save-related indicators
            if (
              data === 'forcesave' ||
              data === 'autosave' ||
              (typeof data === 'object' && (data.save || data.forcesave || data.autosave))
            ) {
              window.dispatchEvent(
                new CustomEvent('onlyoffice-document-saved', {
                  detail: { documentUuid: uuid }
                })
              )
            }
          }
        },
        onDocumentStateChange: function (event: any) {
          // This event fires when document has unsaved changes
          // event.data is true when there are unsaved changes, false when all changes are saved
          // When OnlyOffice reports all changes are saved, try to trigger a manual save
          // by simulating Ctrl+S to force OnlyOffice to send a callback
          if (!event.data) {
            // All changes are saved to cache. OnlyOffice autosave doesn't send callbacks.
            // Try to trigger a manual save by dispatching Ctrl+S keyboard event
            // This should trigger OnlyOffice's save button which sends a callback
            // OnlyOffice autosave saves to cache but doesn't send callbacks to the backend.
            // Since manual save works and sends callbacks, we programmatically click OnlyOffice's
            // save button when autosave completes to trigger the same callback.
            setTimeout(() => {
              try {
                // Find OnlyOffice iframe
                const allIframes = document.querySelectorAll('iframe')
                for (const iframe of Array.from(allIframes)) {
                  try {
                    if (
                      iframe.src &&
                      (iframe.src.includes('onlyoffice') || iframe.src.includes('web-apps'))
                    ) {
                      if (iframe.contentWindow && iframe.contentDocument) {
                        // Try to find and click the save button in OnlyOffice's UI
                        const saveButton = iframe.contentDocument.querySelector(
                          '[title*="Save"], [aria-label*="Save"], .toolbar__btn_save, button[data-name="save"]'
                        )
                        if (saveButton) {
                          ;(saveButton as HTMLElement).click()
                          return
                        }

                        // Fallback: Try to find save button by text content
                        const buttons = iframe.contentDocument.querySelectorAll(
                          'button, a, div[role="button"]'
                        )
                        for (const btn of Array.from(buttons)) {
                          const text = btn.textContent?.toLowerCase() || ''
                          const title = btn.getAttribute('title')?.toLowerCase() || ''
                          if (
                            (text.includes('save') || title.includes('save')) &&
                            !text.includes('autosave')
                          ) {
                            ;(btn as HTMLElement).click()
                            return
                          }
                        }
                      }
                    }
                  } catch (e) {
                    // Cross-origin or other error - silently continue
                  }
                }
              } catch (e) {
                // Silently handle errors
              }
            }, 200) // Small delay to ensure OnlyOffice UI is ready

            window.dispatchEvent(
              new CustomEvent('onlyoffice-document-saved', {
                detail: { documentUuid: uuid }
              })
            )
          }
        }
      }
    })
  }

  function cleanup() {
    if (tokenRefreshTimer.value) {
      clearTimeout(tokenRefreshTimer.value)
    }
    if (navbarChecker.value) {
      clearInterval(navbarChecker.value)
    }
    if (window.docEditor) {
      window.docEditor.destroyEditor()
      window.docEditor = null
    }
  }

  onMounted(async () => {
    if (props.checkForNavbar) {
      detectNavbar()
      navbarChecker.value = setInterval(detectNavbar, 500)
      setTimeout(() => clearInterval(navbarChecker.value), 5000)
    }

    await Promise.all([userStore.fetchUser(), fetchDocument()])
    await fetchEditToken()
    await initializeOnlyOffice()
  })

  onUnmounted(() => {
    cleanup()
  })
</script>

<template>
  <div
    class="office-editor-wrapper"
    :class="{ 'with-navbar': hasNavbar, 'with-sidebar': hasSidebar }"
  >
    <div class="office-editor-container">
      <div
        v-if="isLoading || isRefreshingToken"
        class="loading-container"
      >
        <div class="loading-spinner"></div>
        <p v-if="isLoading">Loading document...</p>
        <p v-else-if="isRefreshingToken">Refreshing access token...</p>
      </div>

      <div
        v-else
        id="office-editor-placeholder"
        class="office-editor"
      ></div>
    </div>
  </div>
</template>

<style scoped>
  .office-editor-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
  }

  .office-editor-wrapper.with-navbar {
    /* Can be adjusted by parent if needed */
  }

  .office-editor-wrapper.with-sidebar {
    /* Can be adjusted by parent if needed */
  }

  .office-editor-container {
    overflow: hidden;
    background: white;
    width: 100%;
    height: 100%;
  }

  .office-editor {
    width: 100%;
    height: 100%;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 2rem;
    text-align: center;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
